import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { CurrencyDecimalInput, CurrencyFixedDecimalInput } from '@/shared/formatters';
import {
  RenterInviteAddressesAutocomplete,
  RenterInviteDatePicker,
  RenterInviteTextInput,
} from '../components';
import { theme } from '@theguarantors/tg-package-material-ui';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
}

export const LeaseInformation = ({ control, errors, watch, setValue, trigger }: Props) => {
  const leaseStart = watch('leaseStart');

  return (
    <>
      <Typography
        variant="h6"
        component="h2"
        fontWeight="bold !important"
        lineHeight="28px"
        mb={1}
        sx={{
          lineHeight: '28px',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
            lineHeight: '24px',
          },
        }}
      >
        Lease information
      </Typography>
      <Typography
        variant="body2"
        fontWeight="350"
        mb={4}
        letterSpacing={0}
        sx={{
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
            lineHeight: '22px',
          },
        }}
      >
        Enter the lease details below. If your applicant is paying for the entire first month
        upfront at lease signing, we don't need to cover it (optional).
      </Typography>
      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          return (
            <RenterInviteAddressesAutocomplete
              required
              size="small"
              value={field.value?.monolith_building_id}
              onBlur={() => field.onBlur()}
              onChange={(value) => field.onChange(value)}
              name={field.name}
              label="Search by address or property name"
              showLoadingSkeleton={true}
              error={!!errors.address}
              helperText={
                errors.address?.monolith_building_id
                  ? errors.address?.monolith_building_id.message?.toString()
                  : errors.address?.message?.toString()
              }
              data-testid="address"
            />
          );
        }}
      />
      <Controller
        name="apartmentNumber"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            id={'apartmentNumber'}
            data-testid="apartmentNumber"
            label="Unit number"
            required
            error={!!errors.apartmentNumber}
            helperText={errors.apartmentNumber?.message?.toString()}
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: 4 }} mt={2}>
        <Controller
          name="leaseStart"
          control={control}
          render={({ field }) => (
            <RenterInviteDatePicker
              value={field.value ? dayjs(field.value) : null}
              label="Lease start date"
              maxDate={dayjs().add(1, 'year')}
              minDate={dayjs().subtract(1, 'year')}
              setValue={(value: dayjs.Dayjs) => {
                if (value?.isValid()) {
                  setValue('leaseStart', value.toDate(), { shouldValidate: true });
                  setValue('leaseEnd', value.add(1, 'year').subtract(1, 'day').toDate(), {
                    shouldValidate: true,
                  });
                }
              }}
              error={errors.leaseStart?.message?.toString()}
              setError={() => trigger('leaseStart')}
              slotProps={{
                textField: {
                  id: 'lease_start',
                  inputProps: {
                    'data-testid': 'lease_start',
                  },
                },
              }}
            />
          )}
        />
        <Controller
          name="leaseEnd"
          control={control}
          render={({ field }) => (
            <RenterInviteDatePicker
              value={field.value ? dayjs(field.value) : null}
              label="Lease end date"
              maxDate={leaseStart ? dayjs(leaseStart).add(10, 'year').add(1, 'month') : undefined}
              minDate={leaseStart ? dayjs(leaseStart).add(1, 'day') : undefined}
              setValue={(value: dayjs.Dayjs) => {
                if (value?.isValid()) {
                  setValue('leaseEnd', value.toDate(), { shouldValidate: true });
                }
              }}
              error={errors.leaseEnd?.message?.toString()}
              setError={() => trigger('leaseEnd')}
              slotProps={{
                textField: {
                  id: 'lease_end',
                  inputProps: {
                    'data-testid': 'lease_end',
                  },
                },
              }}
            />
          )}
        />
      </Stack>
      <Controller
        name="grossMonthlyRent"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            InputProps={{
              inputComponent: CurrencyDecimalInput,
              disableUnderline: true,
            }}
            label="Total monthly rent"
            id={'gross_monthly_rent'}
            data-testid="gross_monthly_rent"
            required
            error={!!errors.grossMonthlyRent}
            helperText={
              errors.grossMonthlyRent?.message?.toString() ??
              'This is the market price rent that is on the lease.'
            }
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction="row" gap={4}>
        <Controller
          name="prepaidRent"
          control={control}
          render={({ field }) => (
            <RenterInviteTextInput
              {...field}
              ref={null}
              size="small"
              InputProps={{
                inputComponent: CurrencyFixedDecimalInput,
                disableUnderline: true,
              }}
              label="Prepaid rent"
              id={'prepaid-rent'}
              data-testid="prepaid-rent"
              error={!!errors.prepaidRent}
              helperText={
                <>
                  <Box>{errors.prepaidRent?.message?.toString()}</Box>
                  <Typography variant="helpertext">
                    Any rent that will be collected at lease signing.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
        <Controller
          name="freeRent"
          control={control}
          render={({ field }) => (
            <RenterInviteTextInput
              {...field}
              ref={null}
              size="small"
              InputProps={{
                inputComponent: CurrencyFixedDecimalInput,
                disableUnderline: true,
              }}
              label="Free rent"
              id={'free-rent'}
              data-testid="free-rent"
              error={!!errors.freeRent}
              helperText={
                <>
                  <Box>{errors.freeRent?.message?.toString()}</Box>
                  <Typography variant="helpertext">
                    Any concessions offered to the renter where they are not responsible for making
                    rent payments.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
      </Stack>
    </>
  );
};
